import {useMap, useMapEvents} from "react-leaflet";
import L from "leaflet";
import {useState, useCallback} from "react";

const AddStops = ({ addingStops, isEditing, setStopMarkers, stopMarkers }) => {
    const [pendingStop, setPendingStop] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmPosition, setConfirmPosition] = useState({ x: 0, y: 0 });

    const map = useMap();

    /**
     * Las paradas SON los puntos de la ruta (ya no hay un "inicio" externo).
     * Encuentra en qué segmento entre dos paradas cae el punto nuevo.
     */
    const calculateInsertIndex = useCallback((newStopLatLng) => {
        if (stopMarkers.length === 0) return 0;
        if (stopMarkers.length === 1) return 1;

        const currentLatLng = L.latLng(newStopLatLng.lat, newStopLatLng.lng);
        const routePoints = stopMarkers.map(s => L.latLng(Number(s.lat), Number(s.lng)));

        let bestIndex = routePoints.length;
        let minDistance = Infinity;

        for (let i = 0; i < routePoints.length - 1; i++) {
            const segmentStart = routePoints[i];
            const segmentEnd = routePoints[i + 1];

            const projection = L.GeometryUtil.closestOnSegment(map, currentLatLng, segmentStart, segmentEnd);
            const distanceToSegment = currentLatLng.distanceTo(projection);

            if (distanceToSegment < minDistance) {
                minDistance = distanceToSegment;
                // El punto cae en el segmento entre la parada i y la i+1,
                // así que siempre va insertado en i+1 (entre ambas).
                bestIndex = i + 1;
            }
        }

        // ¿Cae más allá de la última parada, extendiendo la ruta?
        // Se proyecta sobre la línea SIN recortar al segmento (t puede pasar de 1):
        // si t > 1, el punto queda después del último punto → se agrega al final.
        if (bestIndex === routePoints.length - 1) {
            const A = routePoints[routePoints.length - 2];
            const B = routePoints[routePoints.length - 1];

            const ABx = B.lng - A.lng;
            const ABy = B.lat - A.lat;
            const APx = currentLatLng.lng - A.lng;
            const APy = currentLatLng.lat - A.lat;
            const segLenSq = ABx * ABx + ABy * ABy;

            const t = segLenSq > 0 ? (APx * ABx + APy * ABy) / segLenSq : 0;
            if (t > 1) bestIndex = stopMarkers.length;
        }

        return bestIndex;
    }, [map, stopMarkers]);

    const addMarker = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!pendingStop) return;

        const newMarker = {
            lat: pendingStop.lat,
            lng: pendingStop.lng
        };

        if (isEditing) {
            const insertIndex = calculateInsertIndex(newMarker);
            setStopMarkers(prevWaypoints => {
                const updatedWaypoints = [...prevWaypoints];
                updatedWaypoints.splice(insertIndex, 0, newMarker);
                return updatedWaypoints;
            });
        } else {
            setStopMarkers(prevStops => [...prevStops, newMarker]);
        }

        setPendingStop(null);
        setShowConfirm(false);
    }, [pendingStop, isEditing, calculateInsertIndex, setStopMarkers]);

    const handleMapClick = useCallback((e) => {
        if (!addingStops || showConfirm) return;

        const clickedPoint = e.latlng;
        setPendingStop(clickedPoint);

        // Convertir coordenadas del mapa a coordenadas de la pantalla
        const point = map.latLngToContainerPoint(clickedPoint);
        const mapContainer = map.getContainer();
        const rect = mapContainer.getBoundingClientRect();

        setConfirmPosition({
            x: rect.left + point.x,
            y: rect.top + point.y
        });

        setShowConfirm(true);
    }, [addingStops, showConfirm, map]);

    const handleCancel = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setPendingStop(null);
        setShowConfirm(false);
    }, []);

    useMapEvents({
        click: handleMapClick
    });

    // No renderizar nada si no estamos en modo de agregar paradas
    if (!addingStops) return null;

    return (
        <>
            {showConfirm && (
                <div
                    style={{
                        position: 'fixed',
                        left: `${confirmPosition.x}px`,
                        top: `${confirmPosition.y}px`,
                        transform: 'translate(-50%, -100%)',
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        zIndex: 1000,
                        pointerEvents: 'auto',
                        color: 'black'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <p style={{ margin: '0 0 10px 0', whiteSpace: 'nowrap' }}>
                        ¿Desea agregar un marcador aquí?
                    </p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button
                            onClick={addMarker}
                            style={{
                                padding: '5px 10px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Confirmar
                        </button>
                        <button
                            onClick={handleCancel}
                            style={{
                                padding: '5px 10px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default AddStops;